import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from '../store'

import { Task } from '../../models/MeetingSupport'
import { User } from '../../models/User'

interface TaskState {
  isLoading: boolean
  pollingEnabled: boolean
  tasks: EntityState<Task, string>
}

const taskAdapter = createEntityAdapter<Task>()

const initialState: TaskState = {
  isLoading: true,
  pollingEnabled: false,
  tasks: taskAdapter.getInitialState(),
}

export const taskSlice = createSlice({
  name: 'task',
  initialState,
  reducers: {
    setTasksLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setTasks: (state, action: PayloadAction<Task[]>) => {
      taskAdapter.setAll(state.tasks, action.payload)
    },
    addTasks: (state, action: PayloadAction<Task[]>) => {
      taskAdapter.addMany(state.tasks, action.payload)
    },
    updateTask: (state, action: PayloadAction<Task>) => {
      taskAdapter.setOne(state.tasks, action.payload)
    },
    deleteTask: (state, action: PayloadAction<Task>) => {
      taskAdapter.removeOne(state.tasks, action.payload.id)
    },
    startTaskPolling: (state) => {
      state.pollingEnabled = true
    },
    stopTaskPolling: (state) => {
      state.pollingEnabled = false
    },
    resetTasks: () => initialState,
  },
})

export const { setTasksLoading, setTasks, addTasks, updateTask, deleteTask, startTaskPolling, stopTaskPolling, resetTasks } = taskSlice.actions

export const selectTasksLoading = (state: RootState) => state.task.isLoading

const taskSelectors = taskAdapter.getSelectors((state: RootState) => state.task.tasks)

export const selectTasks = (state: RootState) => taskSelectors.selectAll(state)

export const selectGroupedTasks = createSelector([(state: RootState) => taskSelectors.selectAll(state)], (tasks) => {
  const users = tasks.reduce<User[]>((acc, task) => {
    const assignedToUser = task.assignedTo?.user
    if (!assignedToUser || acc.find((e) => e.id === assignedToUser.id)) {
      return acc
    }
    return [...acc, assignedToUser]
  }, [])

  return users.map((user) => {
    const userTasks = tasks.filter((e) => e.assignedTo?.user?.id === user.id)
    return {
      user,
      tasks: userTasks,
    }
  })
})

export const selectTaskPollingEnabled = (state: RootState) => state.task.pollingEnabled

export default taskSlice.reducer
